/**
 * sectionSplitter — distribute a module draft across its numbered
 * skeleton sub-sections.
 *
 * The agent drafts a whole module as one block with ``## N.x`` numbered
 * sub-headings, but the document skeleton already owns a numbered heading
 * for each sub-section (9.1, 9.2, …). Inserting the whole block under the
 * primary heading therefore (a) renders the agent's ``## N.x`` lines as
 * body text and (b) leaves the skeleton's numbered sub-headings empty.
 *
 * This splits the draft at each ``## N.x`` whose number matches a known
 * skeleton section: the heading line is consumed (the skeleton owns it)
 * and the lines beneath it are routed to that section. Content before the
 * first matching sub-heading stays with ``primary``.
 *
 * Mirrors ``split_draft_by_sections`` in ``src/csr_sim/word_writer.py`` so
 * the Office add-in and the Python .docx export distribute identically.
 *
 * SRP: no I/O. Same input (and known skeleton) → same output.
 */

import { knownSkeletonSections } from "./skeletonSections";

// Numbered markdown heading (## / ### / ####). The section number starts
// with a digit; later dotted segments may be alphanumeric (CMC uses S/P/A/R
// letter codes). Matches the grammar in sectionHeaderLogic.ts / word_writer.py.
const RE_NUMBERED_HEADING = /^(#{1,4})\s+(\d+(?:\.[\dA-Za-z]+)*)\.?\s+.+$/;
// Any single-token heading id (``## TOC  Table of Contents``). Only meaningful
// when the caller checks the id against its declared section set — mirrors
// ``_RE_ID_HEADING`` in word_writer.py (non-numeric front-matter ids).
const RE_ID_HEADING = /^(#{1,4})\s+([A-Za-z0-9][A-Za-z0-9._-]*)\s+.+$/;

/**
 * Split a module draft into a map of ``sectionNumber → content``.
 *
 * Every entry in ``sectionNumbers`` is present in the result (default
 * ``""``) so callers can leave a skeleton heading empty rather than
 * inserting a "see other section" cross-reference. Sub-headings whose
 * number is NOT a skeleton section (e.g. a deeper ``### 9.4.1`` when the
 * skeleton stops at 9.4) are left inline as content under the current
 * section.
 *
 * ``skeletonNumbers`` is the whole document's section numbers (default: the
 * skeleton wordWriter last scaffolded or hydrated; empty = no foreign check).
 * A heading for a skeleton section OUTSIDE this module — neither one of
 * ``sectionNumbers`` nor a descendant of one (``## 8 Discussion`` in §7's
 * draft) — belongs to another module: its block is dropped, with a warning,
 * up to the next heading of this module. Inlining it put §8's text under §7.
 */
export function splitDraftBySections(
  draft: string,
  sectionNumbers: string[],
  primary: string,
  skeletonNumbers?: Iterable<string>,
): Record<string, string> {
  const known = new Set(sectionNumbers);
  const skeleton = new Set(skeletonNumbers ?? knownSkeletonSections());
  // This module's sections and their descendants — plus the primary's
  // ancestors (a draft restating its parent heading keeps the old inline
  // behaviour; only siblings and unrelated sections are foreign).
  const owned = (num: string) =>
    known.has(num) ||
    primary === num ||
    primary.startsWith(`${num}.`) ||
    [...known, primary].some((k) => num.startsWith(`${k}.`));
  const buffers: Record<string, string[]> = {};
  for (const sec of sectionNumbers) buffers[sec] = [];
  if (!buffers[primary]) buffers[primary] = [];

  let current: string | null = primary;
  let foreign: { number: string; lines: number } | null = null;
  const endForeign = () => {
    if (!foreign) return;
    console.warn(
      `[sectionSplitter] §${primary}: dropped a block headed §${foreign.number} (${foreign.lines} line(s)) — that section belongs to another module`,
    );
    foreign = null;
  };
  for (const line of draft.split("\n")) {
    const trimmed = line.trim();
    const m = RE_NUMBERED_HEADING.exec(trimmed) ?? RE_ID_HEADING.exec(trimmed);
    if (m && known.has(m[2])) {
      endForeign();
      current = m[2];
      continue; // consume the heading line — the skeleton owns it
    }
    const numbered = RE_NUMBERED_HEADING.exec(trimmed);
    if (numbered && skeleton.has(numbered[2]) && !owned(numbered[2])) {
      endForeign();
      foreign = { number: numbered[2], lines: 0 };
      current = null;
      continue;
    }
    if (current === null) {
      if (foreign) foreign.lines++;
      continue;
    }
    buffers[current].push(line);
  }
  endForeign();

  const out: Record<string, string> = {};
  for (const sec of Object.keys(buffers)) {
    out[sec] = buffers[sec].join("\n").replace(/^\n+|\n+$/g, "");
  }
  return out;
}
